import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { LANGUAGE_TESTS } from "@/lib/data";

const COURSE_ROUTES = {
  IELTS: "/courses/ielts",
  PTE: "/courses/pte",
  "Spoken English": "/courses/spoken-english",
};

export default function TestPrep() {
  return (
    <section id="test-prep" data-testid="testprep-section" className="py-24 md:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative rounded-3xl overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?q=80&w=1600&auto=format&fit=crop"
                alt="Test preparation"
                loading="lazy"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0F3D8C]/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5">
                <div className="text-xs tracking-[0.25em] uppercase text-white/70">Avg. improvement</div>
                <div className="text-4xl font-heading font-light text-white">+1.5 Bands</div>
                <div className="text-sm text-white/70 mt-1">Across all IELTS students, 2024–25.</div>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">Test Preparation</div>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tighter">
              Master every test. <br />
              <span className="font-serif italic text-gradient-royal">On the first try.</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-lg max-w-lg">
              Data-driven curriculum, unlimited mock tests and one-on-one mentorship for every language and admissions exam.
            </p>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {LANGUAGE_TESTS.map((t, i) => {
                const route = COURSE_ROUTES[t.code];
                const inner = (
                  <>
                    <div className="flex items-center gap-2 mb-1.5">
                      <CheckCircle2 className="w-4 h-4 text-[#0F3D8C] dark:text-[#60A5FA]" />
                      <div className="font-heading font-semibold">{t.code}</div>
                      {route && (
                        <ArrowUpRight className="w-3.5 h-3.5 ml-auto text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground leading-tight">{t.bands}</div>
                  </>
                );
                const wrapClass =
                  "p-4 rounded-2xl bg-card border border-border hover:border-[#D4AF37] hover:-translate-y-0.5 transition-all group block";
                return route ? (
                  <motion.div
                    key={t.code}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                  >
                    <Link
                      to={route}
                      data-testid={`test-badge-${t.code.toLowerCase().replace(/\s+/g, "-")}`}
                      className={wrapClass}
                    >
                      {inner}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.div
                    key={t.code}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    data-testid={`test-badge-${t.code.toLowerCase().replace(/\s+/g, "-")}`}
                    className={wrapClass}
                  >
                    {inner}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
